// @ts-nocheck
import { jsPDF } from "jspdf";

const MARGIN = 48;
const LINE = 16;

/** Google Fonts — covers English + all 9 voice-interview languages. */
const NOTO_FONTS_URL =
  "https://fonts.googleapis.com/css2?" +
  "family=Noto+Sans:wght@400;600;700&" +
  "family=Noto+Sans+Devanagari:wght@400;600;700&" +
  "family=Noto+Sans+Bengali:wght@400;600;700&" +
  "family=Noto+Sans+Tamil:wght@400;600;700&" +
  "family=Noto+Sans+Telugu:wght@400;600;700&" +
  "family=Noto+Sans+Kannada:wght@400;600;700&" +
  "family=Noto+Sans+Malayalam:wght@400;600;700&" +
  "family=Noto+Sans+Gujarati:wght@400;600;700&" +
  "display=swap";

const PRINT_FONT_STACK =
  '"Noto Sans", "Noto Sans Devanagari", "Noto Sans Bengali", "Noto Sans Tamil", ' +
  '"Noto Sans Telugu", "Noto Sans Kannada", "Noto Sans Malayalam", "Noto Sans Gujarati", ' +
  "system-ui, sans-serif";

function slugify(s) {
  return String(s || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "export";
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Original answer + optional English translation (for print layout). */
function formatAnswerForPrint(answerText, answerTextEn, interviewLanguage) {
  const original = (answerText == null ? "" : String(answerText)).trim();
  const en = (answerTextEn == null ? "" : String(answerTextEn)).trim();
  const lang = (interviewLanguage || "English").trim();
  if (!original) return { original: "—", translation: null };
  if (/^english$/i.test(lang) || !en) return { original, translation: null };
  if (en.toLowerCase() === original.toLowerCase()) return { original, translation: null };
  return { original, translation: en };
}

function transcriptDocumentTitle(candidateName, jobTitle) {
  const name = candidateName || "Candidate";
  const role = String(jobTitle || "").trim();
  if (role && role !== "Role") return `${name} — ${role} — Interview Transcript`;
  return `${name} — Interview Transcript`;
}

function buildTranscriptPrintHtml({ candidateName, jobTitle, lang, sections = [], generatedAt = new Date() }) {
  const generated = generatedAt.toLocaleString();
  const roleLine = `${jobTitle || "Role"}${lang ? ` · ${lang}` : ""}`;
  const docTitle = transcriptDocumentTitle(candidateName, jobTitle);

  const sectionHtml = (sections || [])
    .map((section) => {
      const prefix = section.shortLabel || "Q";
      const items = (section.items || [])
        .map((a, idx) => {
          const qno = a.index ?? idx + 1;
          const { original, translation } = formatAnswerForPrint(
            a.answerText,
            a.answerTextEn,
            lang,
          );
          const translationBlock = translation
            ? `<p class="answer-translation"><span class="translation-label">English translation:</span> ${escapeHtml(translation)}</p>`
            : "";
          return `
            <div class="qa-block">
              <p class="question"><strong>${escapeHtml(prefix)} Q${qno}:</strong> ${escapeHtml(a.questionText || "—")}</p>
              <div class="answer">
                <p class="answer-original"><span class="answer-label">A:</span> ${escapeHtml(original)}</p>
                ${translationBlock}
              </div>
            </div>`;
        })
        .join("");
      return `
        <section class="section">
          <h2 class="section-title">${escapeHtml(section.label)} (${section.items?.length || 0})</h2>
          ${items}
        </section>`;
    })
    .join("");

  const bodyContent = sections.length
    ? sectionHtml
    : `<p class="empty">No interview transcript is stored for this application.</p>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(docTitle)}</title>
  <link rel="stylesheet" href="${NOTO_FONTS_URL}" />
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: ${PRINT_FONT_STACK};
      font-size: 10pt;
      line-height: 1.45;
      color: #334155;
      background: #fff;
      padding: 18mm 18mm 28mm;
    }
    .title { font-size: 18pt; font-weight: 700; color: #7c2d4e; margin: 0 0 4px; }
    .candidate { font-size: 13pt; font-weight: 700; color: #0f172a; margin: 0 0 2px; }
    .meta { font-size: 10pt; color: #64748b; margin: 0 0 2px; }
    .generated { font-size: 9pt; color: #94a3b8; margin: 0 0 10px; }
    .rule { border: none; border-top: 1px solid #e2e8f0; margin: 0 0 14px; }
    header { break-after: avoid-page; }
    .section { margin-bottom: 16px; }
    .section-title {
      font-size: 11pt;
      font-weight: 700;
      color: #4f46e5;
      margin: 0 0 8px;
    }
    .qa-block { margin-bottom: 12px; break-inside: avoid-page; }
    .question {
      font-size: 10pt;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 4px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .answer { margin-left: 12pt; }
    .answer-original {
      font-size: 10pt;
      color: #334155;
      margin: 0 0 4px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .answer-label { font-weight: 600; }
    .answer-translation {
      font-size: 9pt;
      color: #64748b;
      font-style: italic;
      margin: 0;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .translation-label { font-style: normal; font-weight: 600; }
    .empty { color: #64748b; font-size: 10pt; }
    .print-footer {
      display: none;
    }
    @media print {
      body { padding: 0 0 16mm; }
      .print-footer {
        display: flex;
        justify-content: space-between;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 6px 18mm 0;
        font-size: 8pt;
        color: #94a3b8;
        border-top: 1px solid #e2e8f0;
      }
      .no-print { display: none !important; }
    }
    @page { size: A4; margin: 18mm; }
  </style>
</head>
<body>
  <header>
    <h1 class="title">Interview Transcript</h1>
    <p class="candidate">${escapeHtml(candidateName || "Candidate")}</p>
    <p class="meta">${escapeHtml(roleLine)}</p>
    <p class="generated">Generated: ${escapeHtml(generated)}</p>
    <hr class="rule" />
  </header>
  <main>${bodyContent}</main>
  <footer class="print-footer">
    <span>Indira IVF · AIRA</span>
    <span class="no-print-hint"></span>
  </footer>
  <p class="no-print" style="margin-top:24px;font-size:9pt;color:#94a3b8;text-align:center;">
    Choose <strong>Save as PDF</strong> in the print dialog to download this transcript.
  </p>
</body>
</html>`;
}

import { showAppToast } from "@/shared/lib/toastBridge";

function openPrintDocument(html, title) {
  const win = window.open("", "_blank");
  if (!win) {
    showAppToast({
      tone: "warning",
      title: "Pop-up blocked",
      message:
        "Please allow pop-ups for this site to export the PDF, then try again.",
      durationMs: 7000,
    });
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.document.title = title || "Interview Transcript";

  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch (_) {}
  };

  win.onafterprint = () => {
    try {
      win.close();
    } catch (_) {}
  };

  if (win.document.fonts && win.document.fonts.ready) {
    win.document.fonts.ready.then(() => setTimeout(triggerPrint, 150)).catch(() => setTimeout(triggerPrint, 400));
  } else {
    setTimeout(triggerPrint, 600);
  }
}

/** Lightweight writer that tracks the cursor and adds pages as needed. */
function createWriter(doc) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - MARGIN * 2;
  let y = MARGIN;

  const ensure = (needed = LINE) => {
    if (y + needed > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const text = (str, { size = 10, style = "normal", color = [30, 41, 59], gap = 0, indent = 0 } = {}) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(str ?? ""), maxWidth - indent);
    lines.forEach((ln) => {
      ensure(LINE);
      doc.text(ln, MARGIN + indent, y);
      y += LINE;
    });
    if (gap) y += gap;
  };

  const rule = (gap = 6) => {
    ensure(gap);
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);
    y += gap;
  };

  const space = (h = LINE) => {
    ensure(h);
    y += h;
  };

  return { text, rule, space, ensure, maxWidth };
}

function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 24, { align: "right" });
    doc.text("Indira IVF · AIRA", MARGIN, pageHeight - 24);
  }
}

/**
 * Export a full interview transcript via browser print (Save as PDF).
 * Uses Unicode fonts so Hindi and other Indic scripts render correctly.
 * sections: [{ label, shortLabel, items: [{ questionText, answerText, answerTextEn, index }] }]
 */
export function exportTranscriptPdf({ candidateName, jobTitle, lang, sections = [], generatedAt = new Date() }) {
  const html = buildTranscriptPrintHtml({ candidateName, jobTitle, lang, sections, generatedAt });
  const title = transcriptDocumentTitle(candidateName, jobTitle);
  openPrintDocument(html, title);
}

/**
 * Export an interview analysis to PDF.
 * data: { rec, summary, tech, comm, strengths: [], areas: [] }
 */
export function exportAnalysisPdf({ candidateName, jobTitle, data = {}, generatedAt = new Date() }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const w = createWriter(doc);

  w.text("Interview Analysis", { size: 18, style: "bold", color: [124, 45, 78] });
  w.text(candidateName || "Candidate", { size: 13, style: "bold", color: [15, 23, 42], gap: 2 });
  w.text(jobTitle || "Role", { size: 10, color: [100, 116, 139] });
  if (data.rec) w.text(`Recommendation: ${data.rec}`, { size: 10, style: "bold", color: [79, 70, 229] });
  w.text(`Generated: ${generatedAt.toLocaleString()}`, { size: 9, color: [148, 163, 184], gap: 6 });
  w.rule(10);

  if (data.summary) {
    w.text("Summary", { size: 11, style: "bold", color: [100, 116, 139], gap: 2 });
    w.text(data.summary, { size: 10, color: [51, 65, 85], gap: 10 });
  }

  if (data.tech != null || data.comm != null) {
    w.text("Scores", { size: 11, style: "bold", color: [100, 116, 139], gap: 2 });
    if (data.tech != null) w.text(`Technical: ${data.tech}/10`, { size: 10, color: [51, 65, 85] });
    if (data.comm != null) w.text(`Communication: ${data.comm}/10`, { size: 10, color: [51, 65, 85] });
    w.space(8);
  }

  if ((data.strengths || []).length) {
    w.text("Strengths", { size: 11, style: "bold", color: [22, 163, 74], gap: 2 });
    data.strengths.forEach((s) => w.text(`• ${s}`, { size: 10, color: [51, 65, 85], indent: 6 }));
    w.space(8);
  }

  if ((data.areas || []).length) {
    w.text("Improvements", { size: 11, style: "bold", color: [217, 119, 6], gap: 2 });
    data.areas.forEach((a) => w.text(`• ${a}`, { size: 10, color: [51, 65, 85], indent: 6 }));
    w.space(8);
  }

  addFooter(doc);
  doc.save(`${slugify(candidateName)}_${slugify(jobTitle)}_analysis.pdf`);
}
